import React, { useState, memo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Check,
  X,
  AlertCircle,
  MessageSquare,
  Clock,
  Shield,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import EvidenceQualityBadge, { EvidenceQualityCard } from './EvidenceQualityBadge';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Evidence Review Panel
 * Internal review actions: Approve, Request More, Reject
 */
function EvidenceReviewPanel({ 
  job, 
  onReviewComplete,
  className = '' 
}) {
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(true);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  if (!job) return null;
  
  const evidence = job.evidence_files || [];
  const currentReview = job.evidence_review || {};
  
  const handleReview = async (action) => {
    if (!reviewNotes.trim() && action !== 'approve') {
      toast.error('Please add review notes');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/api/phs/jobs/${job.id}/evidence-review`,
        {
          action,
          notes: reviewNotes.trim(),
          evidence_count: evidence.length
        },
        { headers }
      );
      
      if (response.data.success) {
        toast.success(
          action === 'approve' ? 'Evidence approved' :
          action === 'reject' ? 'Evidence rejected' :
          'More evidence requested'
        );
        setReviewNotes('');
        setReviewAction(null);
        onReviewComplete?.(response.data);
      }
    } catch (err) {
      console.error('Review failed:', err);
      toast.error(err.response?.data?.detail || 'Review action failed');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${className}`}
      data-testid="evidence-review-panel"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b border-slate-100 active:bg-slate-100 touch-manipulation"
      >
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-teal-600" />
          <span className="font-semibold text-slate-800 text-sm">Evidence Review</span>
          <EvidenceQualityBadge job={job} size="xs" />
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Evidence Quality Card */}
          <EvidenceQualityCard job={job} />
          
          {/* Current Review Status */}
          {currentReview.status && (
            <div className={`p-3 rounded-xl border ${
              currentReview.status === 'approved' ? 'bg-emerald-50 border-emerald-200' :
              currentReview.status === 'rejected' ? 'bg-red-50 border-red-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-700">Current Status</span>
                <Badge className={`text-[10px] ${
                  currentReview.status === 'approved' ? 'bg-emerald-500' :
                  currentReview.status === 'rejected' ? 'bg-red-500' :
                  'bg-blue-500'
                } text-white`}>
                  {currentReview.status === 'more_requested' ? 'More Requested' : currentReview.status}
                </Badge>
              </div>
              {currentReview.notes && (
                <p className="text-xs text-slate-600 mt-2">{currentReview.notes}</p>
              )}
              {currentReview.reviewed_by && (
                <p className="text-[10px] text-slate-400 mt-1">
                  by {currentReview.reviewed_by} • {new Date(currentReview.reviewed_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
          
          {/* Review Actions */}
          {evidence.length > 0 && (
            <div className="space-y-3">
              <span className="text-xs font-medium text-slate-600">Review Actions</span>
              
              {!reviewAction ? (
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-10 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200"
                    onClick={() => setReviewAction('approve')}
                    data-testid="review-approve-btn"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-10 text-xs bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 active:bg-amber-200"
                    onClick={() => setReviewAction('request_more')}
                    data-testid="review-request-more-btn"
                  >
                    <MessageSquare className="h-3.5 w-3.5 mr-1" />
                    Request
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-10 text-xs bg-red-50 border-red-200 text-red-700 hover:bg-red-100 active:bg-red-200"
                    onClick={() => setReviewAction('reject')}
                    data-testid="review-reject-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Reject
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium capitalize">
                      {reviewAction === 'request_more' ? 'Request More Evidence' : `${reviewAction} Evidence`}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs text-slate-500"
                      onClick={() => { setReviewAction(null); setReviewNotes(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                  
                  <Textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder={
                      reviewAction === 'approve' ? 'Optional notes...' :
                      reviewAction === 'reject' ? 'Reason for rejection (required)...' :
                      'What additional evidence is needed (required)...'
                    }
                    className="min-h-[80px] text-sm"
                    data-testid="review-notes-input"
                  />
                  
                  <Button
                    size="sm"
                    className={`w-full h-10 ${
                      reviewAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      reviewAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                      'bg-amber-600 hover:bg-amber-700'
                    }`}
                    onClick={() => handleReview(reviewAction)}
                    disabled={submitting || (reviewAction !== 'approve' && !reviewNotes.trim())}
                    data-testid="review-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Clock className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        {reviewAction === 'approve' && <Check className="h-3.5 w-3.5 mr-1.5" />}
                        {reviewAction === 'reject' && <X className="h-3.5 w-3.5 mr-1.5" />}
                        {reviewAction === 'request_more' && <MessageSquare className="h-3.5 w-3.5 mr-1.5" />}
                        Confirm {reviewAction === 'request_more' ? 'Request' : reviewAction}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* No Evidence Warning */}
          {evidence.length === 0 && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>No evidence uploaded yet. Review will be available once evidence is submitted.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(EvidenceReviewPanel);
